import cx from "classnames";
import React, { type FC, forwardRef, type ReactElement, useRef } from "react";
import { noop as NOOP } from "es-toolkit";
import { useMergeRef, getStyle } from "@vibe/shared";

import { Icon, type IconType, type SubIcon } from "@vibe/icon";
import type VibeComponentProps from "../../../types/VibeComponentProps";
import { ComponentDefaultTestId, getTestId } from "../../../tests/test-ids-utils";
import styles from "./Tab.module.scss";
import { Tooltip, type TooltipProps } from "@vibe/tooltip";
import { ComponentVibeId } from "../../../tests/constants";
import { keyCodes } from "../../../constants";

export interface TabProps extends VibeComponentProps {
  /**
   * Class name applied to the inner tab content.
   */
  tabInnerClassName?: string;
  /**
   * The index value of the tab.
   */
  value?: number;
  /**
   * If true, disables the tab.
   */
  disabled?: boolean;
  /**
   * If true, marks the tab as active.
   */
  active?: boolean;
  /**
   * If true, applies focus styles to the tab.
   */
  focus?: boolean;
  /**
   * If true, hides the individual tab border when using stretched underline.
   */
  stretchedUnderline?: boolean;
  /**
   * The icon displayed in the tab.
   */
  icon?: SubIcon;
  /**
   * The type of icon.
   */
  iconType?: IconType;
  /**
   * The position of the icon relative to the text.
   */
  iconSide?: string;
  /**
   * Callback fired when the tab is clicked.
   */
  onClick?: (value: number) => void;
  /**
   * Props passed to the tab's tooltip.
   */
  tooltipProps?: Partial<TooltipProps>;
  /**
   * The content displayed inside the tab.
   */
  children?: string | ReactElement | ReactElement[];
  /**
   * Tab index for focus management.
   */
  tabIndex?: number;
  /**
   * The id of the associated TabPanel for aria-controls attribute.
   */
  "aria-controls"?: string;
}

const Tab: FC<TabProps> = forwardRef(
  (
    {
      className,
      tabInnerClassName,
      id,
      value = 0,
      disabled = false,
      active = false,
      focus = false,
      stretchedUnderline = false,
      onClick = NOOP,
      tooltipProps = {} as TooltipProps,
      icon,
      iconType,
      iconSide = "left",
      children,
      "data-testid": dataTestId,
      tabIndex,
      "aria-controls": ariaControls
    }: TabProps,
    ref
  ) => {
    const componentRef = useRef(null);
    const mergedRef = useMergeRef(ref, componentRef);

    function renderIconAndChildren() {
      if (!icon) return children;

      const iconElement = (
        <Icon
          aria-hidden={true}
          type={iconType}
          icon={icon}
          className={cx(styles.tabIcon, getStyle(styles, iconSide))}
          size={18}
          ignoreFocusStyle
        />
      );

      const childrenArray = React.Children.toArray(children);

      if (iconSide === "left") {
        return [iconElement, ...childrenArray];
      }

      return [...childrenArray, iconElement];
    }

    function handleKeyDown(event: React.KeyboardEvent) {
      if (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE) {
        event.preventDefault();
        !disabled && onClick(value);
      }
    }

    return (
      <Tooltip {...tooltipProps} content={tooltipProps.content}>
        <li
          ref={mergedRef}
          key={id}
          className={cx(styles.tabWrapper, className, {
            [styles.active]: active,
            [styles.disabled]: disabled,
            [styles.tabFocusVisibleInset]: focus,
            [styles.stretchedUnderline]: stretchedUnderline
          })}
          id={id}
          role="tab"
          aria-selected={active}
          aria-disabled={disabled}
          aria-controls={ariaControls || undefined}
          tabIndex={tabIndex}
          data-testid={dataTestId || getTestId(ComponentDefaultTestId.TAB, id)}
          data-vibe={ComponentVibeId.TAB}
          onClick={() => !disabled && onClick(value)}
          onKeyDown={handleKeyDown}
        >
          <div className={cx(styles.tabInner, tabInnerClassName)}>{renderIconAndChildren()}</div>
        </li>
      </Tooltip>
    );
  }
);

export default Tab;
